import {
  Outlet,
  ScrollRestoration,
  createBrowserRouter,
  type RouteObject,
} from 'react-router-dom';

import { Route } from './Route';
import * as P from '@/constants/paths';
import { SearchBar } from '@/components/shared/SearchBar';

import { Login } from '@/components/screens/Login/Login';
import { Landing } from '@screens/Landing';
import { MyFoodsPage } from '@screens/MyFoodsPage';
import { NutritionPage } from '@screens/NutritionPage';
import { PasswordRecovery } from '@screens/PasswordRecovery';
import { EmailConfirmation } from '@screens/EmailConfirmation';
import { MyIngredientsPage } from '@screens/MyIngredientsPage';
import { SettingsPage } from '@/components/screens/SettingsPage';
import { IngredientDetailPage } from '@screens/IngredientDetailPage';
import { SampleDetailsPage } from '@/components/screens/SampleDetailsPage';
import { SendMyReportsPage } from '@/components/screens/SendMyReportsPage';
import { MyHealthHistoryPage } from '@/components/screens/MyHealthHistoryPage';
import { AccountManagementPage } from '@/components/screens/AccountManagementPage';
import { TrackSamplesStatusPage } from '@/components/screens/TrackSamplesStatusPage';
import { NutritionFoodDetailPage } from '@screens/MyFoodsDetailPage/MyFoodsDetailPage';
import { HealthPage } from '@screens/HealthPages/HealthPage';
import { HealthConnectionsPage } from '@screens/HealthConnections';
import { HealthConditionsPage } from '@screens/HealthConditions';
import { HealthConditionDetailPage } from '@screens/HealthConditionsDetails';
import { ActiveMicrobesPage } from '@screens/ActiveMicrobes';
import { L2DetailPage } from '@screens/LevelTwoDetail/L2DetailPage';
import { BioAgePage } from '@screens/BioAge';
import { InflammagingPage } from '@screens/Imflammaging';
import { GutMicrobiomePage } from '@screens/GutMicrobiome';
import { OralMicrobiomePage } from '@screens/OralMicrobiome/OralMicrobiomePage';
import { OralDybiosisPage } from '@screens/OralDybiosis';
import { GutDybiosisPage } from '@screens/GutDybiosis';
import { OralRichnessPage } from '@screens/OralRichness';
import { GutRichnessPage } from '@screens/GutRichness';
import { HealthContributingScore } from '@screens/HealthPages/HealthContributingScore';
import { HealthL3DetailsPage } from '@screens/HealthPages/HealthL3DetailsPage';
import { HealthL4DetailsPage } from '@screens/HealthPages/HealthL4DetailsPage';
import { ShopPage } from '@screens/ShopPage';
import { QuestionnairesPage } from '@screens/QuestionnairesPage';
import { RegistrationLanding } from '@screens/registration/RegistrationLanding';
import { RegistrationKitId } from '@screens/registration/RegistrationKitId';
import { RegistrationAccountCreation } from '@screens/registration/RegistrationAccountCreation';
import { RegistrationPersonalInformation } from '@screens/registration/RegistrationPersonalInformation';
import { RegistrationContactInformation } from '@screens/registration/RegistrationContactInformation';
import { RegistrationConfirmation } from '@screens/registration/RegistrationConfirmation';
import { RegistrationTermsOfService } from '@screens/registration/RegistrationTermsOfService';
import { RegistrationPendingKit } from '@screens/registration/RegistrationPendingKit';
import { RegistrationMinorConsent } from '@screens/registration/RegistrationMinorConsent';
import { RegistrationBundleInfo } from '@screens/registration/RegistrationBundleInfo';
import { RegistrationAddressConfirmation } from '@screens/registration/RegistrationAddressConfirmation';

import { DashboardLayout } from '@layouts/DashboardLayout';
import { ResultsReady } from '@screens/HomePage/ResultReady/ResultsReady';
import { BoostersAndBlockersPage } from '@screens/HomePage/MyBoosterandBlockerPage/BoostersandBlockersPage';
import { PlantVirusPage } from '@screens/HomePage/PlantVirusPage/PlantVirusPage';
import { BloodSugarResponse } from '@screens/HomePage/BloodSugarResponsePage/BloodSugarResponse';
import { MoodMemoryFocusPage } from '@screens/HomePage/MoodMemoryAndFocusPage/MoodMemoryFocusPage';
import { CollectSamplePage } from '@screens/HomePage/CollectSamplePage';
import { SMP } from '@/components/screens/SMP';
import { HealthL4ImproveMyScoreDetail } from '@screens/HealthPages/HealthL4ImproveMyScoreDetail';

import { BootupGuard } from './guards/BootupGuard';
import { SignUpProvider } from '@/contexts/SignupContext';
import { Toaster } from '@/components/design-system/toaster';
import { CancerDetectPage } from '@screens/CancerDetectPage';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { BoosterBlockerDetailPage } from '@screens/HomePage/MoodMemoryAndFocusPage/BoostersAndBlockersDetailPage';

const ROUTING_CONFIG: RouteObject[] = [
  {
    element: (
      <Route>
        <ScrollRestoration />
        <Outlet />
      </Route>
    ),
    children: [
      {
        path: P.LOGIN_PATH,
        element: <Login />,
      },
      {
        path: P.LANDING_PATH,
        element: <Landing />,
      },
    ],
  },
  {
    element: (
      <Route fromRegistration>
        <ScrollRestoration />
        <SignUpProvider>
          <Toaster />
          <Outlet />
        </SignUpProvider>
      </Route>
    ),
    children: [
      // Registration
      {
        path: P.REGISTRATION_LANDING,
        element: <RegistrationLanding />,
      },
      {
        path: P.REGISTRATION_KIT_ID,
        element: <RegistrationKitId />,
      },
      {
        path: P.REGISTRATION_ACCOUNT_CREATION,
        element: <RegistrationAccountCreation />,
      },
      {
        path: P.REGISTRATION_PERSONAL_INFORMATION,
        element: <RegistrationPersonalInformation />,
      },
      {
        path: P.REGISTRATION_CONTACT_INFORMATION,
        element: <RegistrationContactInformation />,
      },
      {
        path: P.REGISTRATION_CONFIRMATION,
        element: <RegistrationConfirmation />,
      },
      {
        path: P.REGISTRATION_TOS,
        element: <RegistrationTermsOfService />,
      },
      {
        path: P.REGISTRATION_PENDING_KIT,
        element: <RegistrationPendingKit />,
      },
      {
        path: P.REGISTRATION_MINOR_CONSENT,
        element: <RegistrationMinorConsent />,
      },
      {
        path: P.REGISTRATION_BUNDLE_INFO,
        element: <RegistrationBundleInfo />,
      },
      {
        path: P.REGISTRATION_ADDRESS_CONFIRMATION,
        element: <RegistrationAddressConfirmation />,
      },
      {
        path: P.PASSWORD_RECOVERY_PATH,
        element: <PasswordRecovery />,
      },
      {
        path: P.EMAIL_CONFIRMATION_PATH,
        element: <EmailConfirmation />,
      },
    ],
  },
  {
    element: (
      <Route isProtected>
        <ScrollRestoration />
        <DashboardLayout />
      </Route>
    ),
    children: [
      {
        element: (
          <BootupGuard>
            <SearchBar>
              <Outlet />
            </SearchBar>
          </BootupGuard>
        ),
        children: [
          {
            path: P.HOME_PATH,
            element: <ResultsReady />,
          },
          {
            path: P.MY_FOODS_PATH,
            element: <MyFoodsPage />,
          },
          {
            path: P.NUTRITION_PATH,
            element: <NutritionPage />,
          },
          {
            path: P.FOOD_DETAILS_PATH,
            element: <NutritionFoodDetailPage />,
          },
          {
            path: P.FORMULA_PATH,
            element: <MyIngredientsPage />,
          },
          {
            path: P.INGREDIENT_DETAILS_PATH,
            element: <IngredientDetailPage />,
          },
          {
            path: P.HEALTH_PATH,
            element: <HealthPage />,
          },
          {
            path: P.HEALTH_CONNECTIONS_PATH,
            element: <HealthConnectionsPage />,
          },
          {
            path: P.HEALTH_CONDITIONS_PATH,
            element: <HealthConditionsPage />,
          },
          {
            path: P.HEALTH_CONDITION_DETAIL_PATH,
            element: <HealthConditionDetailPage />,
          },
          {
            path: P.ACTIVE_MICROBES_PATH,
            element: <ActiveMicrobesPage />,
          },
          {
            path: P.L2_SCORE_PATH,
            element: <L2DetailPage />,
          },
          {
            path: P.HEALTH_CONTRIBUTING_SCORE,
            element: <HealthContributingScore />,
          },
          {
            path: P.L2_CONTRIBUTING_SCORE,
            element: <HealthContributingScore />,
          },
          {
            path: P.L3_DETAIL_PATH,
            element: <HealthL3DetailsPage />,
          },
          {
            path: P.L4_DETAIL_PATH,
            element: <HealthL4DetailsPage />,
          },
          {
            path: P.BIO_AGE_PATH,
            element: <BioAgePage />,
          },
          {
            path: P.INFLAMMAGING_PATH,
            element: <InflammagingPage />,
          },
          {
            path: P.GUT_MICROBIOME_PATH,
            element: <GutMicrobiomePage />,
          },
          {
            path: P.ORAL_MICROBIOME_PATH,
            element: <OralMicrobiomePage />,
          },
          {
            path: P.GUT_RICHNESS_PATH,
            element: <GutRichnessPage />,
          },
          {
            path: P.GUT_DYBIOSIS_PATH,
            element: <GutDybiosisPage />,
          },
          {
            path: P.ORAL_RICHNESS_PATH,
            element: <OralRichnessPage />,
          },
          {
            path: P.ORAL_DYBIOSIS_PATH,
            element: <OralDybiosisPage />,
          },
          {
            path: P.IMPROVE_MY_SCORE_DETAIL,
            element: <HealthL4ImproveMyScoreDetail />,
          },
          {
            path: P.RESULTS_READY_PATH,
            element: <ResultsReady />,
          },
          {
            path: P.BOOSTERS_AND_BLOCKERS_PATH,
            element: <BoostersAndBlockersPage />,
          },
          {
            path: P.PLANT_VIRUS_PATH,
            element: <PlantVirusPage />,
          },
          {
            path: P.BLOOD_SUGAR_PATH,
            element: <BloodSugarResponse />,
          },
          {
            path: P.MOOD_MEMORY_FOCUS_PATH,
            element: <MoodMemoryFocusPage />,
          },
          {
            path: P.COLLECT_SAMPLE_PATH,
            element: <CollectSamplePage />,
          },
          {
            path: P.SHOP_PATH,
            element: <ShopPage />,
          },
          {
            path: P.QUESTIONNAIRES_PATH,
            element: <QuestionnairesPage />,
          },
          {
            path: P.SMP_PATH,
            element: <SMP />,
          },
          {
            path: P.CANCER_DETECT_PATH,
            element: <CancerDetectPage />,
          },
          {
            path: P.ACCOUNT_MANAGEMENT_PATH,
            element: <AccountManagementPage />,
          },
          {
            path: P.SETTINGS_PATH,
            element: <SettingsPage />,
          },
          {
            path: P.MY_HEALTH_HISTORY_PATH,
            element: <MyHealthHistoryPage />,
          },
          {
            path: P.TRACK_SAMPLE_STATUS_PATH,
            element: <TrackSamplesStatusPage />,
          },
          {
            path: P.SAMPLE_DETAILS_PATH,
            element: <SampleDetailsPage />,
          },
          {
            path: P.MY_REPORTS_PATH,
            element: <SendMyReportsPage />,
          },
          {
            path: P.BOOSTER_BLOCKER_DETAIL,
            element: <BoosterBlockerDetailPage />
          },
        ],
      },
      {
        path: '*', 
        element: <ErrorLoadingPage hideNavBar errorType='notFound' />, 
      },
    ],
  },
];

export const router = createBrowserRouter(ROUTING_CONFIG);
